import { createApi } from '@reduxjs/toolkit/query/react'
import baseQuery from './baseQuery'

/**
 * Freight Profitability panel (Reports & Analytics → Freight Profitability).
 * Backend: proship_mongo `/api/finance/freight-profitability/*` — ROLE_PROSHIPADMIN only.
 * Endpoints return the DTO directly (not wrapped in GenericAPIResponse), hence `res?.data || res`.
 */
const apiBase = 'finance/freight-profitability'

/**
 * The shared baseQuery throws the raw axios error; RTK Query then serialises it down to { name, message } and the
 * backend's message ("Order-level data is limited to 7 days…", "A recompute is already running…") is lost — the page
 * only ever saw "Request failed with status code 400". Return an RTK-style error instead so `error.status` and
 * `error.data.message` (the GlobalControllerExceptionHandler body) survive to the components.
 */
const freightPnlBaseQuery = async (args, api, extra) => {
  try {
    return await baseQuery(args, api, extra)
  } catch (err) {
    const status = err?.response?.status
    const data = err?.response?.data
    return { error: { status, data, message: data?.message || err?.message || 'Request failed' } }
  }
}

export const freightPnlApi = createApi({
  reducerPath: 'freightPnlApi',
  baseQuery: freightPnlBaseQuery,
  keepUnusedDataFor: 5 * 60,
  tagTypes: ['MarginTargets'],
  endpoints: (builder) => ({

    // { segment, from, to, merchantOids, courierOids, lossMakingOnly, lossThresholdPct }
    getFreightPnlSummary: builder.query({
      query: (filters) => ({ url: `${apiBase}/summary`, data: filters, type: 'POST' }),
      transformResponse: (res) => res?.data || res,
    }),

    // same scope + { priced, page (1-based), pageSize }
    getFreightPnlOrders: builder.query({
      query: (filters) => ({ url: `${apiBase}/orders`, data: filters, type: 'POST' }),
      transformResponse: (res) => res?.data || res,
      keepUnusedDataFor: 60,
    }),

    // { from, to, segment? } → 202 with { requestId, state, ... }
    recomputeFreightPnl: builder.mutation({
      query: (body) => ({ url: `${apiBase}/recompute`, data: body, type: 'POST' }),
      transformResponse: (res) => res?.data || res,
    }),

    getFreightPnlRecomputeStatus: builder.query({
      query: (requestId) => ({ url: `${apiBase}/recompute/${requestId}`, type: 'GET' }),
      transformResponse: (res) => res?.data || res,
      keepUnusedDataFor: 0,
    }),

    // Margin vs each merchant's target and vs last year, last 7 days and month to date (PACE+ margin report).
    getFreightMarginTargets: builder.query({
      query: (asOf) => ({ url: `${apiBase}/margin-targets`, data: asOf ? { asOf } : {}, type: 'GET' }),
      transformResponse: (res) => res?.data || res,
      providesTags: ['MarginTargets'],
    }),

    // pct null / '' clears the target.
    setFreightMarginTarget: builder.mutation({
      query: ({ merchantOid, pct }) => ({
        url: `${apiBase}/margin-targets/${merchantOid}${pct === null || pct === undefined || pct === '' ? '' : `?pct=${encodeURIComponent(pct)}`}`,
        type: 'PUT',
      }),
      transformResponse: (res) => res?.data || res,
      invalidatesTags: ['MarginTargets'],
    }),
  }),
})

export const {
  useGetFreightPnlSummaryQuery,
  useGetFreightPnlOrdersQuery,
  useRecomputeFreightPnlMutation,
  useGetFreightPnlRecomputeStatusQuery,
  useGetFreightMarginTargetsQuery,
  useSetFreightMarginTargetMutation,
} = freightPnlApi
